import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Clock, 
  Calculator, 
  FileText, 
  BookOpen,
  User,
  ChevronRight,
  RotateCcw,
  Info
} from 'lucide-react';
import { QuestionComponent } from './QuestionTypes';
import KatexRenderer from './KatexRenderer';
import WatermarkedQuestion from './antiCheating/WatermarkedQuestion';
import { useRealtime } from '../contexts/RealtimeContext';

// Custom scrollbar styles
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #dbeafe;
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #93c5fd;
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #60a5fa;
  }
  .custom-scrollbar::-webkit-scrollbar-corner {
    background: #dbeafe;
  }
`;

const CATTestInterface = ({ 
  questions, 
  answers, 
  onAnswerSelect, 
  onTestSubmit,
  testInfo,
  timeLeft,
  isSubmitting = false,
  questionTimes = {},
  autoSaveEnabled = true,
  onAutoSaveToggle,
  sessionId = null,
  testId = null,
  onQuestionChange = null
}) => {
  const { currentUser, userProfile } = useRealtime();
  const [markedForReview, setMarkedForReview] = useState(new Set());
  const [visitedQuestions, setVisitedQuestions] = useState(new Set());
  const [showCalculator, setShowCalculator] = useState(false);
  const [showQuestionPaper, setShowQuestionPaper] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [filteredQuestionIndex, setFilteredQuestionIndex] = useState(0);

  const userName = userProfile?.name || userProfile?.displayName || 'Candidate';

  // CAT sections - use from testInfo or default CAT sections
  const sections = testInfo?.sections && testInfo.sections.length > 0 ? testInfo.sections : [
    { name: 'Verbal Ability and Reading Comprehension', shortName: 'VARC' },
    { name: 'Data Interpretation and Logical Reasoning', shortName: 'DILR' },
    { name: 'Quantitative Ability', shortName: 'QA' }
  ];

  // Filter questions by section
  useEffect(() => {

    if (testInfo?.useDynamicSections && testInfo?.sections && testInfo.sections.length > 0) {
      // Check if sections have proper startIndex and questionCount
      const hasValidSections = testInfo.sections.every(s => 
        typeof s.startIndex === 'number' && typeof s.questionCount === 'number'
      );
      
      if (hasValidSections) {
        const sectionQuestions = questions.filter((q, idx) => {
          const section = testInfo.sections.find(s => 
            idx >= s.startIndex && idx < s.startIndex + s.questionCount
          );
          return section && testInfo.sections.indexOf(section) === selectedSectionIndex;
        });
        setFilteredQuestions(sectionQuestions);
      } else {
        // If sections don't have proper structure, divide questions equally
        const questionsPerSection = Math.ceil(questions.length / testInfo.sections.length);
        const startIdx = selectedSectionIndex * questionsPerSection;
        const endIdx = Math.min(startIdx + questionsPerSection, questions.length);
        const sectionQuestions = questions.slice(startIdx, endIdx);
        setFilteredQuestions(sectionQuestions);
      }
    } else {
      // If no sections or not using dynamic sections, use all questions
      setFilteredQuestions(questions);
    }
  }, [questions, selectedSectionIndex, testInfo]);

  // Mark question as visited
  useEffect(() => {
    if (filteredQuestions.length > 0 && filteredQuestionIndex < filteredQuestions.length) {
      setVisitedQuestions(prev => new Set([...prev, filteredQuestionIndex]));
    }
  }, [filteredQuestionIndex, filteredQuestions.length]);

  const currentQuestion = filteredQuestions[filteredQuestionIndex];
  const currentAnswer = answers[filteredQuestionIndex];

  // Get actual question index in the full questions array
  const getActualQuestionIndex = useCallback(() => {
    if (!currentQuestion) return 0;
    return questions.findIndex(q => q.id === currentQuestion.id);
  }, [currentQuestion, questions]);

  const actualQuestionIndex = getActualQuestionIndex();

  // Notify parent when question changes
  useEffect(() => {
    if (onQuestionChange && currentQuestion) {
      onQuestionChange(actualQuestionIndex, currentQuestion.id);
    }
  }, [actualQuestionIndex, currentQuestion?.id, onQuestionChange]);

  // Check if current question is RC or LRDI Set and get passage/set content
  const { hasPassage, passageContent, isRC, isLRDISet } = useMemo(() => {
    if (!currentQuestion) return { hasPassage: false, passageContent: null, isRC: false, isLRDISet: false };
    
    // Enhanced detection for RC and LRDI questions
    const questionType = currentQuestion.type;
    const isRCQuestion = questionType === 'rc' || questionType === 'RC' || questionType === 'reading-comprehension';
    const isLRDIQuestion = questionType === 'lrdi' || questionType === 'LRDI' || questionType === 'lrdi-set' || questionType === 'data-interpretation';
    
    // Direct passage content check
    if (currentQuestion?.passage) {
      return { 
        hasPassage: true, 
        passageContent: currentQuestion.passage,
        isRC: true,
        isLRDISet: false
      };
    }
    
    // Direct set content check
    if (currentQuestion?.setContent) {
      return { 
        hasPassage: true, 
        passageContent: currentQuestion.setContent,
        isRC: false,
        isLRDISet: true
      };
    }
    
    // Check for RC/LRDI indicators in question properties
    if (isRCQuestion || currentQuestion?.isRC || currentQuestion?.questionType === 'RC' || 
        currentQuestion?.tags?.includes('RC') || currentQuestion?.tags?.includes('Reading Comprehension') ||
        currentQuestion?.category === 'RC' || currentQuestion?.subject === 'Reading Comprehension') {
      
      // Try to find passage in testInfo
      let passage = null;
      if (testInfo?.passages && Array.isArray(testInfo.passages)) {
        if (currentQuestion?.passageGroupId || currentQuestion?.passageId) {
          const passageId = currentQuestion.passageGroupId || currentQuestion.passageId;
          passage = testInfo.passages.find(p => p.id === passageId);
        } else {
          // Find passage by question index
          passage = testInfo.passages.find(p => 
            p.questionIndices?.includes(filteredQuestionIndex) ||
            (p.startIndex !== undefined && 
             p.endIndex !== undefined &&
             filteredQuestionIndex >= p.startIndex &&
             filteredQuestionIndex <= p.endIndex)
          );
        }
        if (passage) {
          return { 
            hasPassage: true, 
            passageContent: passage.content || passage.text || passage.passage || passage.data || null,
            isRC: true,
            isLRDISet: false
          };
        }
      }
      
      // Return false if no passage found
      return { hasPassage: false, passageContent: null, isRC: true, isLRDISet: false };
    }
    
    // Check for LRDI indicators
    if (isLRDIQuestion || currentQuestion?.isLRDI || currentQuestion?.questionType === 'LRDI' || 
        currentQuestion?.tags?.includes('LRDI') || currentQuestion?.tags?.includes('Data Interpretation') ||
        currentQuestion?.category === 'LRDI' || currentQuestion?.subject === 'Data Interpretation') {
      
      // Try to find set content in testInfo
      let setData = null;
      if (testInfo?.sets && Array.isArray(testInfo.sets)) {
        if (currentQuestion?.setGroupId || currentQuestion?.setId) {
          const setId = currentQuestion.setGroupId || currentQuestion.setId;
          setData = testInfo.sets.find(s => s.id === setId);
        } else {
          setData = testInfo.sets.find(s => 
            s.questionIndices?.includes(filteredQuestionIndex) ||
            (s.startIndex !== undefined && 
             s.endIndex !== undefined &&
             filteredQuestionIndex >= s.startIndex &&
             filteredQuestionIndex <= s.endIndex)
          );
        }
        if (setData) {
          return { 
            hasPassage: true, 
            passageContent: setData.content || setData.text || setData.data || setData.setContent || null,
            isRC: false,
            isLRDISet: true
          };
        }
      }
      
      // Return false if no set found
      return { hasPassage: false, passageContent: null, isRC: false, isLRDISet: true };
    }
    
    return { hasPassage: false, passageContent: null, isRC: false, isLRDISet: false };
  }, [currentQuestion, selectedSectionIndex, testInfo, filteredQuestionIndex, filteredQuestions]);

  // Format time - "Time Left : 00:35:32"
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get marking scheme - prefer question-specific, then test-level defaults
  const markingScheme = useMemo(() => {
    const defaultScheme = { correct: 3, incorrect: -1, partial: 0 };
    const questionScheme = currentQuestion?.markingScheme || {};
    const testScheme = testInfo?.markingScheme || {};
    return {
      correct: questionScheme.correct ?? testScheme.correct ?? defaultScheme.correct,
      incorrect: questionScheme.incorrect ?? testScheme.incorrect ?? defaultScheme.incorrect,
      partial: questionScheme.partial ?? testScheme.partial ?? defaultScheme.partial
    };
  }, [currentQuestion, testInfo]);

  // Question status helpers
  const getQuestionStatus = (index) => {
    if (!visitedQuestions.has(index)) return 'not-visited';
    if (markedForReview.has(index) && answers[index] !== undefined) return 'answered-review';
    if (markedForReview.has(index)) return 'marked-review';
    if (answers[index] !== undefined) return 'answered';
    return 'not-answered';
  };

  const getQuestionStatusCounts = () => {
    const counts = {
      'not-visited': 0,
      'not-answered': 0,
      'answered': 0,
      'marked-review': 0,
      'answered-review': 0
    };
    filteredQuestions.forEach((_, idx) => {
      counts[getQuestionStatus(idx)]++;
    });
    return counts;
  };

  const statusCounts = getQuestionStatusCounts();

  // Navigation handlers
  const handleQuestionClick = (index) => {
    setFilteredQuestionIndex(index);
  };

  const handleNext = () => {
    if (filteredQuestionIndex < filteredQuestions.length - 1) {
      setFilteredQuestionIndex(filteredQuestionIndex + 1);
    }
  };

  const handleSaveAndNext = () => {
    handleNext();
  };

  const handleMarkForReviewAndNext = () => {
    setMarkedForReview(prev => new Set([...prev, filteredQuestionIndex]));
    handleNext();
  };

  const handleClearResponse = () => {
    onAnswerSelect(filteredQuestionIndex, undefined);
    setMarkedForReview(prev => {
      const newSet = new Set(prev);
      newSet.delete(filteredQuestionIndex);
      return newSet;
    });
  };

  const handleAnswerSelect = (answer) => {
    onAnswerSelect(filteredQuestionIndex, answer);
  };

  // Simple calculator component
  const CalculatorModal = () => {
    const [calcDisplay, setCalcDisplay] = useState('0');
    const [calcExpression, setCalcExpression] = useState('');

    const handleNumber = (num) => {
      if (calcDisplay === '0') {
        setCalcDisplay(num);
      } else {
        setCalcDisplay(calcDisplay + num);
      }
    };

    const handleOperator = (op) => {
      setCalcExpression(calcDisplay + op);
      setCalcDisplay('0');
    };

    const handleEquals = () => {
      try {
        const result = eval(calcExpression + calcDisplay);
        setCalcDisplay(result.toString());
        setCalcExpression('');
      } catch (e) {
        setCalcDisplay('Error');
      }
    };

    const handleClear = () => {
      setCalcDisplay('0');
      setCalcExpression('');
    };

    if (!showCalculator) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCalculator(false)}>
        <div className="bg-white rounded-lg shadow-xl p-6 w-80" onClick={(e) => e.stopPropagation()}>
          <div className="mb-4">
            <div className="bg-gray-100 p-4 rounded text-right text-2xl font-mono min-h-[60px] flex items-center justify-end">
              {calcDisplay}
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[7, 8, 9, '/', 4, 5, 6, '*', 1, 2, 3, '-', 0, '.', '=', '+'].map((item) => (
              <button
                key={item}
                onClick={() => {
                  if (typeof item === 'number' || item === '.') {
                    handleNumber(item.toString());
                  } else if (item === '=') {
                    handleEquals();
                  } else {
                    handleOperator(item);
                  }
                }}
                className={`p-4 rounded text-lg font-semibold ${
                  typeof item === 'number' || item === '.'
                    ? 'bg-gray-200 hover:bg-gray-300'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {item}
              </button>
            ))}
            <button
              onClick={handleClear}
              className="col-span-2 p-4 rounded bg-red-500 text-white hover:bg-red-600 font-semibold"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-300">
        {/* Top Row */}
        <div className="px-4 py-2 flex items-center justify-between bg-gray-800">
          <div className="flex items-center space-x-4">
            <h1 className="text-sm font-semibold text-white">{testInfo?.name || 'Real CAT Mock 22 (RC 22) 2025'}</h1>
          </div>
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowCalculator(true)}
                className="flex items-center space-x-1 text-xs bg-orange-500 text-white px-2 py-1 rounded hover:bg-orange-600"
                title="Calculator"
              >
                <Calculator className="h-4 w-4" />
                <span>Calculator</span>
              </button>
              <button
                onClick={() => setShowQuestionPaper(true)}
                className="flex items-center space-x-1 text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                title="Question Paper"
              >
                <FileText className="h-4 w-4" />
                <span>Question Paper</span>
              </button>
              <button
                onClick={() => setShowInstructions(true)}
                className="flex items-center space-x-1 text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                title="View Instruction"
              >
                <BookOpen className="h-4 w-4" />
                <span>View Instruction</span>
              </button>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <span className="text-white">Time Left :</span>
              <span className="font-semibold text-white">{formatTime(timeLeft)}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                <User className="h-5 w-5 text-white" />
              </div>
              <span className="text-sm font-medium text-white">{userName}</span>
            </div>
          </div>
        </div>

        {/* Section Tabs Row */}
        <div className="px-4 py-1 bg-white">
          <div className="flex items-center space-x-1">
            <span className="text-sm text-gray-700 mr-2">Sections:</span>
            {sections.map((section, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setSelectedSectionIndex(idx);
                  setFilteredQuestionIndex(0);
                }}
                className={`px-3 py-1 text-xs font-medium rounded ${
                  selectedSectionIndex === idx
                    ? 'bg-sky-500 text-white'
                    : 'bg-white text-sky-500 hover:bg-sky-50'
                }`}
              >
                {section.shortName || section.name}
              </button>
            ))}

          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden bg-white">
        {/* Left Panel - Passage/Set (for RC and LRDI Set questions) */}
        {hasPassage && passageContent && (
          <div className="w-1/2 border-r-2 border-blue-200 bg-blue-50 flex flex-col">
            {/* Fixed Header */}
            <div className="flex-shrink-0 p-4 border-b border-blue-200">
              <div className="bg-blue-100 p-3 rounded-lg">
                <h3 className="font-semibold text-blue-800 mb-2">
                  {isRC ? '📖 Reading Comprehension' : isLRDISet ? '📊 Data Interpretation & Logical Reasoning' : '📄 Passage'}
                </h3>
                <p className="text-blue-700 text-sm italic">
                  {isRC 
                    ? 'Read the following passage and answer the questions that follow.'
                    : isLRDISet
                    ? 'Study the following set and answer the questions that follow.'
                    : 'Read the following and answer the questions that follow.'}
                </p>
              </div>
            </div>
            
            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-hidden">
              <div 
                className="h-full overflow-y-auto overflow-x-auto p-4 custom-scrollbar"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#93c5fd #dbeafe'
                }}
                onWheel={(e) => {
                  // Prevent page scroll when scrolling within passage
                  e.stopPropagation();
                }}
                onTouchMove={(e) => {
                  // Prevent page scroll on mobile when scrolling within passage
                  e.stopPropagation();
                }}
              >
                <div className="prose prose-sm max-w-none text-gray-800 bg-white p-4 rounded-lg shadow-sm min-h-full">
                  <KatexRenderer content={passageContent} />
                </div>
              </div>
            </div>
            
            {/* Scroll Indicator */}
            <div className="flex-shrink-0 p-2 bg-blue-100 border-t border-blue-200">
              <div className="text-xs text-blue-600 text-center">
                💡 Scroll within this area to read the complete {isRC ? 'passage' : 'set'}
              </div>
            </div>
          </div>
        )}

        {/* Middle/Center Panel - Question Content */}
        <div className={`${hasPassage ? 'flex-1' : 'flex-1'} overflow-y-auto bg-white`}>
          {currentQuestion && (
            <div className="p-6">
              {/* Question Number */}
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Question No. {filteredQuestionIndex + 1}.
                </h2>
              </div>

              {/* Question Content */}
              <div className="mb-6 text-gray-800 leading-relaxed">
                {sessionId && testId ? (
                  <WatermarkedQuestion
                    question={currentQuestion}
                    questionIndex={filteredQuestionIndex}
                    testId={testId}
                    sessionId={sessionId}
                    testInfo={testInfo}
                  />
                ) : (
                  <KatexRenderer content={currentQuestion.question} />
                )}
              </div>

              {/* Answer Options */}
              <div className="space-y-3">
                <QuestionComponent
                  question={currentQuestion}
                  selectedAnswer={currentAnswer}
                  onAnswerSelect={handleAnswerSelect}
                />
              </div>
            </div>
          )}
          
          {/* Show message if no question */}
          {!currentQuestion && (
            <div className="p-6">
              <div className="text-center text-gray-500">
                <p>No questions available for this test.</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Question Palette */}
        <div className="w-80 bg-white border-l border-gray-300 flex flex-col">
          {/* Marking Scheme at Top */}
          <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
            <div className="text-right text-sm text-gray-600">
              <span>Mark/s: <span className="text-green-600 font-semibold">{markingScheme.correct.toFixed(2)}</span> | Negative Mark/s: <span className="text-red-600 font-semibold">{Math.abs(markingScheme.incorrect).toFixed(2)}</span></span>
            </div>
          </div>

          {/* User Profile Section */}
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex items-center justify-center mb-2">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <User className="h-6 w-6 text-orange-600" />
              </div>
            </div>
            <div className="text-center text-sm font-medium text-gray-900 mb-3">{userName.toUpperCase()}</div>
            
            {/* Status Legend - 2 items per row */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center space-x-1">
                <div className="w-5 h-5 bg-gray-300 rounded flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-semibold">{statusCounts['not-visited']}</span>
                </div>
                <span className="text-xs">Not Visited</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-5 h-5 bg-red-500 rounded flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-semibold text-white">{statusCounts['not-answered']}</span>
                </div>
                <span className="text-xs">Not Answered</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-5 h-5 bg-green-500 rounded flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-semibold text-white">{statusCounts['answered']}</span>
                </div>
                <span className="text-xs">Answered</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-5 h-5 bg-purple-500 rounded flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-semibold text-white">{statusCounts['marked-review']}</span>
                </div>
                <span className="text-xs">Marked for Review</span>
              </div>
              <div className="col-span-2 flex items-center space-x-1">
                <div className="w-5 h-5 bg-purple-500 rounded flex items-center justify-center relative flex-shrink-0">
                  <span className="text-xs font-semibold text-white">{statusCounts['answered-review']}</span>
                </div>
                <span className="text-xs leading-tight">Answered & Marked for Review(will be considered for evaluation)</span>
              </div>
            </div>
          </div>

          {/* Question Palette */}
          <div className="flex-1 overflow-y-auto bg-sky-100">
            <div className="px-4 py-3">
              <div className="mb-3 text-sm font-medium text-gray-700">Choose a question</div>
              <div className="grid grid-cols-4 gap-2">
                {/* Generate 24 questions in 6 rows x 4 columns */}
                {Array.from({ length: 24 }, (_, idx) => {
                  const status = getQuestionStatus(idx);
                  const isCurrent = idx === filteredQuestionIndex;
                  let bgColor = 'bg-gray-300';
                  let textColor = 'text-gray-700';
                  let shapeStyle = {};
                  
                  if (isCurrent) {
                    bgColor = 'bg-orange-500';
                    textColor = 'text-white';
                  } else {
                    if (status === 'answered-review') {
                      bgColor = 'bg-purple-500';
                      textColor = 'text-white';
                      // Hexagon with shorter top (answered)
                      shapeStyle = { clipPath: 'polygon(25% 0%, 75% 0%, 100% 40%, 100% 100%, 0% 100%, 0% 40%)' };
                    } else if (status === 'marked-review') {
                      bgColor = 'bg-purple-500';
                      textColor = 'text-white';
                    } else if (status === 'answered') {
                      bgColor = 'bg-green-500';
                      textColor = 'text-white';
                      // Hexagon with shorter top (answered)
                      shapeStyle = { clipPath: 'polygon(25% 0%, 75% 0%, 100% 40%, 100% 100%, 0% 100%, 0% 40%)' };
                    } else if (status === 'not-answered') {
                      bgColor = 'bg-red-500';
                      textColor = 'text-white';
                      // Hexagon with shorter bottom (not answered)
                      shapeStyle = { clipPath: 'polygon(0% 0%, 100% 0%, 100% 60%, 75% 100%, 25% 100%, 0% 60%)' };
                    } else {
                      bgColor = 'bg-gray-300';
                      textColor = 'text-gray-700';
                    }
                  }

                  return (
                    <button
                      key={idx}
                      onClick={() => handleQuestionClick(idx)}
                      style={shapeStyle}
                      className={`w-10 h-10 text-sm font-semibold ${Object.keys(shapeStyle).length === 0 ? 'rounded' : ''} ${bgColor} ${textColor} hover:opacity-90 transition-opacity flex items-center justify-center`}
                      disabled={idx >= filteredQuestions.length}
                    >
                      {idx + 1}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Navigation Buttons */}
      <div className="bg-white border-t border-gray-300 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={handleMarkForReviewAndNext}
            className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200 text-sm font-medium"
          >
            Mark for review and Next
          </button>
          <button
            onClick={handleClearResponse}
            className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200 text-sm font-medium"
          >
            Clear Response
          </button>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleSaveAndNext}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
          >
            Save & Next
          </button>
          <button
            onClick={onTestSubmit}
            disabled={isSubmitting}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Section'}
          </button>
        </div>
      </div>

      {/* Calculator Modal */}
      <CalculatorModal />
      </div>
    </>
  );
};

export default CATTestInterface;
